import React, { Component } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { v4 as uuidv4 } from 'uuid';
import { baseUrlAdmin } from '../../store/apiConstants';
import Service from '../../services';

class NTTPaymentComponent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isNTTScriptLoaded: false,
            isProcessingPayment: false,
            showNTTPaymentDialog: false,
            nttPaymentError: null
        };
        this.toast = React.createRef();
        this.service = new Service();
    }

    componentDidMount() {
        // this.loadNTTDataScript();
    }

    componentDidUpdate(prevProps) {
        // If parent toggled showPaymentButton true and we have payment data, start payment automatically
        if (this.props.showPaymentButton && !prevProps.showPaymentButton && this.props.paymentData) {
            this.initiateNTTPayment(this.props.paymentData);
        }
    }

    componentWillUnmount() {
        // Clean up intervals and event listeners
        if (this.paymentWindowInterval) {
            clearInterval(this.paymentWindowInterval);
        }
        if (this.windowFocusHandler) {
            window.removeEventListener('focus', this.windowFocusHandler);
        }
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        }
    }

    /**
     * Load NTT Data (Atom) SDK script dynamically
     */
    loadNTTDataScript = () => {
        return new Promise((resolve, reject) => {
            // If already available, resolve
            if (window.AtomPaynetz || window.NTTDataCheckout) {
                this.setState({ isNTTScriptLoaded: true });
                resolve();
                return;
            }

            // Use the SDK URL from environment
            let sdkUrl = '';
            if (localStorage.getItem('username') === 'avanttpayment') {
                sdkUrl = 'https://pgtest.atomtech.in/staticdata/ots/js/atomcheckout.js';
            } else {
                sdkUrl = process.env.REACT_APP_NTT_DATA_SDK_URL;
            }

            // Validate SDK URL is configured
            if (!sdkUrl) {
                const msg = 'NTT Data SDK URL not configured. Please set REACT_APP_NTT_DATA_SDK_URL in environment variables.';
                console.error(msg);
                this.setState({ nttPaymentError: msg });
                reject(new Error(msg));
                return;
            }

            // Check if a script for the same host already exists
            try {
                const parsedHost = new URL(sdkUrl).hostname;
                const existingScript = Array.from(document.scripts).find((s) =>
                    s.src && s.src.includes(parsedHost)
                );
                if (existingScript) {
                    existingScript.onload = () => {
                        this.setState({ isNTTScriptLoaded: true });
                        resolve();
                    };
                    existingScript.onerror = (err) => {
                        const msg = 'Failed to load Atom/NTT SDK from configured URL';
                        console.error(msg, err);
                        this.setState({ nttPaymentError: msg });
                        reject(err || new Error(msg));
                    };
                    return;
                }
            } catch (e) {
                // ignore URL parse errors and proceed to inject script
            }

            const script = document.createElement('script');
            script.src = sdkUrl + (sdkUrl.includes('?') ? '&v=' : '?v=') + Date.now();
            script.async = true;

            script.onload = () => {
                console.log('NTT/Atom SDK loaded successfully from', sdkUrl);
                this.setState({ isNTTScriptLoaded: true });
                resolve();
            };

            //     try {
            script.onerror = (error) => {
                console.error('Failed to load NTT/Atom SDK:', error);
                const msg = 'Failed to load NTT/Atom payment gateway script';
                this.setState({ nttPaymentError: msg });
                reject(new Error(msg));
            };
            // } catch (e) {
            //     // write toaster notification for script loading error
            //     this.showToast('error', 'Failed to load NTT/Atom SDK script');
            //     console.error('Error setting onerror handler for NTT/Atom SDK script:', e);
            // }

            document.head.appendChild(script);
        });
    };

    /**
     * Initiate NTT/Atom payment process - encryption handled in backend
     */
    initiateNTTPayment = async (paymentData) => {
        try {
            this.setState({ isProcessingPayment: true, showNTTPaymentDialog: true, nttPaymentError: null });

            // Generate orderId with length restriction (max 50 characters)
            const timestamp = Date.now().toString();
            const uuid = uuidv4().replace(/-/g, '').substring(0, 16); // Remove hyphens and limit length
            const orderId = `ORD_${uuid}_${timestamp}`.substring(0, 50); // Ensure max 50 chars

            // Prepare payload for backend - wrap in paymentData object as expected by controller
            const paymentPayload = {
                paymentData: {
                    orderId,
                    amount: Math.floor(Number(paymentData.amount) * 100) / 100,
                    currency: 'INR',
                    studentId: paymentData.studentId,
                    installmentId: paymentData.installmentId,
                    feeType: paymentData.feeType || 'fee collect',
                    customerEmail: paymentData.studentEmail,
                    customerPhone: paymentData.studentPhone,
                    academicYear: localStorage.getItem('userAcademicYear'),
                    branchId: paymentData.branchId,
                    boardId: paymentData.boardId,
                    classId: paymentData.classId,
                    sectionId: paymentData.sectionId,
                    orgId: paymentData.orgId,
                    feeFor: paymentData.feeFor,
                    studentName: paymentData.studentName
                }
            };

            console.log('Sending payment payload with orderId:', orderId, 'length:', orderId.length);

            // Send to backend for processing using Service class
            const url = `${baseUrlAdmin}/ntt-payment/create-order`;
            const createOrderResponse = await this.service.post(url, paymentPayload, true);

            if (!createOrderResponse?.status) {
                throw new Error(createOrderResponse?.res?.message || 'Failed to create NTT/Atom order');
            }

            const orderData = createOrderResponse.res.data || {};

            // Load SDK after order is created successfully
            if (!this.state.isNTTScriptLoaded) {
                await this.loadNTTDataScript();
            }

            // Backend returns atomTokenId and other required data
            const atomToken = orderData.atomTokenId;
            const merchantId = orderData.merchantId;
            const custEmail = orderData.customerEmail || "test.user@atomtech.in";
            const custMobile = orderData.customerPhone || "8888888888";

            if (!atomToken) {
                throw new Error('Missing atomTokenId from backend create-order response');
            }

            // Build options expected by Atom SDK
            // Construct return URL with query parameters
            const baseReturnUrl =
                process.env.REACT_APP_NTT_DATA_RETURN_URL ||
                `${window.location.origin}/collectFee`; // Use current origin for return URL

            // Build query parameters to include in return URL
            const returnUrlParams = new URLSearchParams();
            if (paymentData.studentId) {
                returnUrlParams.set('studentId', paymentData.studentId);
            }
            if (paymentData.installmentId) {
                returnUrlParams.set('installmentId', paymentData.installmentId);
            }
            if (paymentData.branchId) {
                returnUrlParams.set('branchId', paymentData.branchId);
            }
            if (paymentData.boardId) {
                returnUrlParams.set('boardId', paymentData.boardId);
            }
            if (paymentData.classId) {
                returnUrlParams.set('classId', paymentData.classId);
            }
            if (paymentData.sectionId) {
                returnUrlParams.set('sectionId', paymentData.sectionId);
            }
            if (paymentData.amount) {
                returnUrlParams.set('amount', paymentData.amount);
            }
            // if (paymentData.pathname) {
            returnUrlParams.set('pathName', window.location.pathname);
            // }

            paymentData.orgId = localStorage.getItem('selectedOrgId') || localStorage.getItem('orgId');
            console.log('Added orgId to return URL params:', paymentData.orgId);
            if (paymentData.orgId) {
                returnUrlParams.set('orgId', paymentData.orgId);
            }
            // Add academic year
            const academicYear = localStorage.getItem('userAcademicYear');
            if (academicYear) {
                returnUrlParams.set('academicYear', academicYear);
            }

            const returnUrl = baseReturnUrl + (returnUrlParams.toString() ? `?${returnUrlParams.toString()}` : '');

            console.log('Return URL with parameters:', returnUrl);


            const options = {
                atomTokenId: atomToken,
                merchId: merchantId,
                custEmail: custEmail,
                custMobile: custMobile,
                returnUrl
            };

            // SDK environment: 'uat' for test, 'prod' for production
            const envRaw = (process.env.REACT_APP_ATOM_ENV || 'uat').toLowerCase();
            const sdkEnv = envRaw === 'production' || envRaw === 'prod' ? 'prod' : 'uat';

            // Set up payment window monitoring
            this.setupPaymentWindowMonitoring();

            // Atom SDK usage: new AtomPaynetz(options, 'uat'|'prod')
            if (window.AtomPaynetz) {
                try {
                    /* eslint-disable no-new */
                    new window.AtomPaynetz(options, sdkEnv);
                    /* eslint-enable no-new */

                    if (this.toast.current) {
                        this.toast.current.show({
                            severity: 'info',
                            summary: 'Payment Window Opened',
                            detail: 'Please complete your payment in the popup/redirect window',
                            life: 4000
                        });
                    }
                } catch (atomError) {
                    console.warn('AtomPaynetz SDK error (likely X-Frame-Options related):', atomError);
                    // If there's an iframe/popup issue, this is expected due to Atom's X-Frame-Options header
                    // The payment should still proceed, so we don't treat this as a fatal error

                    if (this.toast.current) {
                        this.toast.current.show({
                            severity: 'info',
                            summary: 'Payment Redirecting',
                            detail: 'Redirecting to secure payment page...',
                            life: 4000
                        });
                    }
                }
            } else {
                throw new Error('AtomPaynetz SDK not available on window after load');
            }
        } catch (error) {
            console.error('NTT Data payment initiation error:', error);
            this.setState({
                isProcessingPayment: false,
                showNTTPaymentDialog: false,
                nttPaymentError: error.message || 'Payment initiation failed'
            });

            if (this.toast.current) {
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Payment Error in NTT Payment component',
                    detail: error.message || 'Failed to initiate payment',
                    life: 5000
                });
            }
        }
    };

    /**
     * Setup monitoring for payment window closure
     */
    setupPaymentWindowMonitoring = () => {
        // Clear any existing interval
        if (this.paymentWindowInterval) {
            clearInterval(this.paymentWindowInterval);
        }

        // Monitor for payment window events
        let paymentWindowCheckCount = 0;
        const maxChecks = 300; // 5 minutes (300 * 1000ms)

        this.paymentWindowInterval = setInterval(() => {
            paymentWindowCheckCount++;

            // Check if payment is still processing
            if (!this.state.isProcessingPayment || !this.state.showNTTPaymentDialog) {
                clearInterval(this.paymentWindowInterval);
                return;
            }

            // If we've been checking for too long, assume user cancelled
            if (paymentWindowCheckCount >= maxChecks) {
                console.log('Payment window monitoring timeout - auto-closing dialog');
                this.handlePaymentWindowClosed();
                clearInterval(this.paymentWindowInterval);
                return;
            }

            // Check if user has navigated away or closed the payment window
            // This is a fallback mechanism as the SDK should handle most scenarios
            if (document.hidden || !document.hasFocus()) {
                // Document is hidden, but this might just mean user switched tabs
                // We'll rely on other mechanisms for now
            }
        }, 1000);

        // Set up event listeners for window focus events
        const handleWindowFocus = () => {
            if (this.state.showNTTPaymentDialog && this.state.isProcessingPayment) {
                // Window regained focus, check if payment was completed
                // This is mainly for detecting when user returns from payment
                setTimeout(() => {
                    if (this.state.showNTTPaymentDialog && this.state.isProcessingPayment) {
                        // Still showing dialog after focus return, might be cancelled
                        console.log('Window focus returned but payment still processing - checking status');
                    }
                }, 2000);
            }
        };

        const handleBeforeUnload = () => {
            if (this.paymentWindowInterval) {
                clearInterval(this.paymentWindowInterval);
            }
        };

        window.addEventListener('focus', handleWindowFocus);
        window.addEventListener('beforeunload', handleBeforeUnload);

        // Store references to remove listeners later
        this.windowFocusHandler = handleWindowFocus;
        this.beforeUnloadHandler = handleBeforeUnload;
    };

    /**
     * Handle payment window closed/cancelled
     */
    handlePaymentWindowClosed = () => {
        console.log('Payment window closed or cancelled');

        // Clear monitoring interval
        if (this.paymentWindowInterval) {
            clearInterval(this.paymentWindowInterval);
        }

        // Remove event listeners
        if (this.windowFocusHandler) {
            window.removeEventListener('focus', this.windowFocusHandler);
        }
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        }

        // Close dialog and reset state
        this.setState({
            isProcessingPayment: false,
            showNTTPaymentDialog: false
        });

        // Call the cancel handler
        this.handleNTTDataPaymentCancel();
    };

    /**
     * Handle successful NTT Data payment - send to backend for verification
     */
    handleNTTDataPaymentSuccess = async (paymentResult, orderId) => {
        try {
            console.log('NTT Data Payment Success:', paymentResult);

            // Clean up monitoring when payment is successful
            if (this.paymentWindowInterval) {
                clearInterval(this.paymentWindowInterval);
            }

            const verificationPayload = {
                encData: paymentResult.encData,
                orderId: orderId,
                paymentResult: paymentResult
            };

            // Send encrypted response to backend for decryption and verification using Service class
            const url = `${baseUrlAdmin}/ntt-payment/verify-payment`;
            const verificationResponse = await this.service.post(url, verificationPayload, true);

            if (verificationResponse?.status && verificationResponse?.res?.success) {
                this.setState({
                    isProcessingPayment: false,
                    showNTTPaymentDialog: false
                });

                if (this.toast.current) {
                    this.toast.current.show({
                        severity: 'success',
                        summary: 'Payment Successful',
                        detail: 'Fee payment completed successfully',
                        life: 5000
                    });
                }

                if (this.props.onPaymentSuccess) {
                    this.props.onPaymentSuccess(verificationResponse.res.data);
                }
            } else {
                throw new Error(verificationResponse?.res?.message || 'Payment verification failed');
            }
        } catch (error) {
            console.error('NTT Data payment verification error:', error);
            this.setState({
                isProcessingPayment: false,
                showNTTPaymentDialog: false,
                nttPaymentError: error.message || 'Payment verification failed'
            });

            if (this.toast.current) {
                this.toast.current.show({
                    severity: 'error',
                    summary: 'Payment Verification Failed',
                    detail: error.message || 'Payment could not be verified',
                    life: 5000
                });
            }

            if (this.props.onPaymentError) {
                this.props.onPaymentError(error);
            }
        }
    };

    /**
     * Handle payment cancellation
     */
    handleNTTDataPaymentCancel = () => {
        console.log('NTT Data payment cancelled by user');
        this.setState({
            isProcessingPayment: false,
            showNTTPaymentDialog: false
        });

        if (this.toast.current) {
            this.toast.current.show({
                severity: 'warn',
                summary: 'Payment Cancelled',
                detail: 'Payment was cancelled by user',
                life: 3000
            });
        }

        if (this.props.onPaymentCancel) {
            this.props.onPaymentCancel();
        }
    };

    /**
     * Reset NTT payment state
     */
    resetNTTPaymentState = () => {
        // Clean up monitoring
        if (this.paymentWindowInterval) {
            clearInterval(this.paymentWindowInterval);
        }
        if (this.windowFocusHandler) {
            window.removeEventListener('focus', this.windowFocusHandler);
        }
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
        }

        this.setState({
            isProcessingPayment: false,
            showNTTPaymentDialog: false,
            nttPaymentError: null
        });
    };

    render() {
        const { isProcessingPayment, showNTTPaymentDialog, nttPaymentError } = this.state;

        return (
            <div className="ntt-payment-component">
                <Toast ref={this.toast} />

                {/* NTT Payment Processing Dialog */}
                <Dialog
                    visible={showNTTPaymentDialog}
                    style={{ width: '400px' }}
                    header="Processing Payment"
                    modal
                    closable={true}
                    onHide={this.handlePaymentWindowClosed}
                    draggable={false}
                >
                    <div className="flex flex-column align-items-center p-4">
                        <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                        <p className="mt-3 text-center">
                            {isProcessingPayment
                                ? 'Please complete your payment in the NTT Data payment window...'
                                : 'Initializing payment gateway...'}
                        </p>
                        <small className="text-muted text-center mt-2">
                            Do not close this window or refresh the page
                        </small>

                        {/* Cancel Button */}
                        <div className="mt-4">
                            <Button
                                label="Cancel Payment"
                                className="p-button-outlined p-button-secondary"
                                onClick={this.handlePaymentWindowClosed}
                                size="small"
                            />
                        </div>
                    </div>
                </Dialog>

                {/* Error Display */}
                {/* {nttPaymentError && (
                    <div className="p-message p-message-error mt-2">
                        <div className="p-message-wrapper">
                            <span className="p-message-icon pi pi-times-circle"></span>
                            <span className="p-message-text">{nttPaymentError}</span>
                        </div>
                    </div>
                )} */}

                {/* NTT Payment Button */}
                {/* {this.props.showPaymentButton && (
                    <Button
                        label="Pay with NTT Data"
                        className="p-button-success"
                        style={{
                            backgroundColor: '#076EFF',
                            border: 'none',
                            borderRadius: '5px',
                            marginTop: '10px'
                        }}
                        onClick={() => this.initiateNTTPayment(this.props.paymentData)}
                        disabled={isProcessingPayment || !this.state.isNTTScriptLoaded}
                        loading={isProcessingPayment}
                    />
                )} */}
            </div>
        );
    }
}

export default NTTPaymentComponent;
